import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import api, { mediaUrl } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useCurrency } from "@/context/CurrencyContext";

const BagDrawer = () => {
  const { bagOpen, setBagOpen, bag, removeFromBag, updateQty, addToBag, settings } = useStore();
  const { format } = useCurrency();
  const [suggest, setSuggest] = useState(null);
  const nav = useNavigate();
  const suggestSlug = settings?.bag_suggestion_slug;

  useEffect(() => {
    if (bagOpen && bag.length && !suggest && suggestSlug) {
      api.get(`/products/${suggestSlug}`).then((r) => setSuggest(r.data.product)).catch(() => {});
    }
  }, [bagOpen, bag.length, suggest, suggestSlug]);

  const hasPrice = bag.some((i) => i.price != null);
  const subtotal = bag.reduce((n, i) => n + (i.price || 0) * i.qty, 0);

  return (
    <>
      <div className={`fixed inset-0 z-50 bg-espresso/40 transition-opacity duration-500 ${bagOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={() => setBagOpen(false)} />
      <aside data-testid={TID.bagDrawer}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-sand text-espresso flex flex-col transition-transform duration-500 ${bagOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-espresso/10">
          <p className="overline">Your Selection</p>
          <button onClick={() => setBagOpen(false)} aria-label="Close"><X strokeWidth={1.4} /></button>
        </div>

        {bag.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <p className="font-serif text-3xl mb-3">Your selection is waiting.</p>
            <p className="text-sm text-espresso/60 mb-6">Nothing here yet — the world is open.</p>
            <Link to="/collections/drop-001" onClick={() => setBagOpen(false)} className="text-[0.72rem] tracking-[0.2em] uppercase border border-espresso px-6 py-3 hover:bg-espresso hover:text-cream transition-colors">Discover DROP 001</Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {bag.map((i, idx) => (
                <div key={i.key} className="flex gap-4">
                  <img src={mediaUrl(i.image)} alt={i.name} className="w-20 h-28 object-cover" loading="lazy" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <p className="font-serif text-lg leading-tight">{i.name}</p>
                      <button onClick={() => removeFromBag(i.key)} data-testid={TID.bagRemove(idx)} aria-label="Remove"><X size={15} /></button>
                    </div>
                    <p className="text-xs text-espresso/60 mt-1">{i.colour} · {i.size}</p>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex items-center border border-espresso/20">
                        <button onClick={() => updateQty(i.key, i.qty - 1)} className="w-7 h-7 text-sm">–</button>
                        <span className="w-8 text-center text-sm">{i.qty}</span>
                        <button onClick={() => updateQty(i.key, i.qty + 1)} className="w-7 h-7 text-sm">+</button>
                      </div>
                      <span className="text-sm">{format(i.price)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {suggest && (
              <div className="px-6 py-4 border-t border-espresso/10 bg-ivory/50">
                <p className="overline mb-3">Complete Her Story</p>
                <div className="flex items-center gap-3">
                  <img src={mediaUrl(suggest.colors?.[0]?.main_image)} alt={suggest.name} className="w-14 h-16 object-cover" loading="lazy" />
                  <div className="flex-1">
                    <p className="font-serif text-base leading-tight">{suggest.name}</p>
                    <p className="text-xs text-espresso/50">{format(suggest.price)}</p>
                  </div>
                  <button aria-label="Add suggestion"
                    onClick={() => { const c = suggest.colors[0]; const s = c.sizes[0]; addToBag({ productId: suggest.id, slug: suggest.slug, name: suggest.name, colour: c.name, size: s.size, price: suggest.price, image: c.main_image, qty: 1 }); }}
                    className="w-8 h-8 border border-espresso flex items-center justify-center hover:bg-espresso hover:text-cream transition-colors"><Plus size={15} /></button>
                </div>
              </div>
            )}

            <div className="px-6 py-5 border-t border-espresso/10 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-espresso/60">Subtotal</span>
                <span>{hasPrice ? format(subtotal) : "Price on request"}</span>
              </div>
              <button data-testid={TID.bagCheckout} onClick={() => { setBagOpen(false); nav("/checkout"); }}
                className="w-full bg-espresso text-cream py-4 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-cocoa transition-colors">
                View Your Bag
              </button>
              <button data-testid={TID.bagContinue} onClick={() => setBagOpen(false)}
                className="w-full py-3 text-[0.72rem] tracking-[0.2em] uppercase hover-underline mx-auto block">Continue Shopping</button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default BagDrawer;
